import { bookingRequestApi } from 'apiConnector/quote/dynamicBooking';
import * as apiErrorHelper from 'epic/apiErrorEpicHelper';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics/tabRemoved';
import { ofType } from 'redux-observable';
import { merge, NEVER, type Observable, of, type SchedulerLike } from 'rxjs';
import { catchError, filter, map, mergeMap, startWith, takeUntil, withLatestFrom } from 'rxjs/operators';
import type { Action } from 'state/actions';
import type { OrdersBasketForm } from 'state/orders-basket/models';
import { bookingAcked, bookingAckFailed } from 'state/quotes/booking/booking.actions';
import type { EpicWithThunk } from 'state/state.common';
import type { IFrontLogger } from 'utils/loggers/FrontLogger';

/**
 * Maximum number of concurrent booking requests that can be processed simultaneously
 */
const BOOKING_REQUEST_CONCURRENCY_LIMIT = 5;

/**
 * Emits an action immediately and then waits indefinitely until cancelled.
 * This pattern is used to emit acknowledgment actions while keeping the observable
 * alive until completion signals are received.
 */
function emitActionAndWaitForCompletion<T>(action: T): Observable<T> {
  return NEVER.pipe(startWith(action));
}

/**
 * Creates completion signals that indicate when a booking request should complete.
 */
function createCompletionSignals(action$: Observable<Action>, formId: string, quoteId: string) {
  const allBookingResultsReceived$ = action$.pipe(
    ofType('ORDERS_BASKET_ALL_BOOKING_RESULTS_RECEIVED'),
    filter(action => action.formId === formId),
  );

  const bookingFailed$ = action$.pipe(
    ofType('BOOKING_FAILED'),
    filter(action => action.workspace === 'ordersBasket' && (action.formId === formId || action.formId === quoteId)),
  );

  return merge(allBookingResultsReceived$, bookingFailed$);
}

/**
 * Prepares the booking request data from the form.
 */
function prepareBookingRequestData(form: OrdersBasketForm, quoteId: string, frontReconciliationId: string) {
  const { result, negotiationId, version, productSubType } = form;
  return {
    negotiationId,
    quoteId,
    version,
    quoteQuery: result?.quoteQuery,
    productSubType,
    reconciliationId: frontReconciliationId,
  };
}

/**
 * Handles booking request errors with proper logging and error transformation.
 */
function handleBookingError(frontLogger: IFrontLogger, tabId: string, quoteId: string, workspace: 'ordersBasket') {
  return (error: Error) => {
    frontLogger.error('An error occurred during dynamic booking request {@FrontError}', error);
    return of(
      bookingAckFailed(
        tabId,
        quoteId,
        apiErrorHelper.toApiErrorMessage(error, {
          timeoutErrorMessage: 'booking.errorMessages.timeout',
        }),
        workspace,
      ),
    );
  };
}

export function requestBookingEpic(_appScheduler?: SchedulerLike): EpicWithThunk {
  return (
    action$,
    state$,
    { oldHttpClient, frontLogger, selectors: { getOrdersBasketFormById }, getSgmeConfiguration },
  ) =>
    action$.pipe(
      ofType('BOOKING_REQUESTED'),
      filter(action => action.workspace === 'ordersBasket'),
      mergeMap(({ tabId, frontReconciliationId, formId, quoteId, workspace }) => {
        const completion$ = createCompletionSignals(action$, formId, quoteId);

        return of({ tabId, frontReconciliationId, formId, quoteId, workspace }).pipe(
          withLatestFrom(state$),
          mergeMap(([_, state]) => {
            const form = getOrdersBasketFormById(state, formId);

            if (!form) {
              return of(
                bookingAckFailed(
                  tabId,
                  quoteId,
                  apiErrorHelper.toApiErrorMessage(new Error('Form not found')),
                  workspace,
                ),
              );
            }

            const bookingAckTimeoutMs = getSgmeConfiguration().bookingAckTimeoutMs;
            const tabRemoved$ = tabRemoved(action$, tabId, state$);
            const requestData = prepareBookingRequestData(form, quoteId, frontReconciliationId);

            return bookingRequestApi(oldHttpClient, bookingAckTimeoutMs)
              .requestBookingV2(requestData, 'ordersBasket')
              .pipe(
                map(quoteAck =>
                  bookingAcked(
                    tabId,
                    formId,
                    quoteAck.negotiationId,
                    frontReconciliationId,
                    workspace,
                    quoteAck.newVersion,
                  ),
                ),
                mergeMap(ackAction => emitActionAndWaitForCompletion(ackAction)),
                takeUntil(merge(tabRemoved$, completion$)),
                catchError(handleBookingError(frontLogger, tabId, quoteId, 'ordersBasket')),
              );
          }),
          takeUntil(completion$),
        );
      }, BOOKING_REQUEST_CONCURRENCY_LIMIT),
    );
}
